import { Button, Checkbox, Group, List, Modal, Select, Stack, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getDistrictMapOptions } from '@/api/main/@tanstack/react-query.gen'
import type { ControlDataSchema, RegulatedZoneSsdrMode } from '@/api/main/types.gen'

const DEFAULT_SCHEMA = 'control_data_next'

const DEFAULT_RZ_SSDR_MODE: RegulatedZoneSsdrMode = 'single_candidate'

// Keyed by the generated `RegulatedZoneSsdrMode` union so a backend enum change surfaces as a type error here.
const RZ_SSDR_MODE_LABEL_KEY: Record<RegulatedZoneSsdrMode, string> = {
  single_candidate: 'rzModeSingleCandidate',
  all_covered_by: 'rzModeAllCoveredBy',
  all_intersects: 'rzModeAllIntersects',
}

export interface SsdrFlags {
  include_technical_segments: boolean
  include_regulated_zones: boolean
  include_sign_posts: boolean
  exclude_frozen: boolean
  rz_ssdr_mode: RegulatedZoneSsdrMode
}

interface Props {
  opened: boolean
  onClose: () => void
  schemas: ControlDataSchema[]
  isSubmitting: boolean
  onSubmit: (schema: string, districtId: number | null, flags: SsdrFlags) => void
}

interface FormValues extends SsdrFlags {
  schema: string
  district_id: string
}

const FLAG_KEYS = [
  'include_technical_segments',
  'include_regulated_zones',
  'include_sign_posts',
  'exclude_frozen',
] as const

const FLAG_LABEL_KEY: Record<(typeof FLAG_KEYS)[number], string> = {
  include_technical_segments: 'includeTechnicalSegments',
  include_regulated_zones: 'includeRegulatedZones',
  include_sign_posts: 'includeSignPosts',
  exclude_frozen: 'excludeFrozen',
}

type Step = 'form' | 'confirm'

export function ComputeSsdrModal({ opened, onClose, schemas, isSubmitting, onSubmit }: Props) {
  const { t } = useTranslation('controlData')
  const [step, setStep] = useState<Step>('form')

  const defaultSchema = useMemo(() => {
    const names = schemas.map(s => s.schema_name)
    return names.includes(DEFAULT_SCHEMA) ? DEFAULT_SCHEMA : (names[0] ?? '')
  }, [schemas])

  const form = useForm<FormValues>({
    initialValues: {
      schema: defaultSchema,
      district_id: '',
      include_technical_segments: false,
      include_regulated_zones: false,
      include_sign_posts: false,
      exclude_frozen: false,
      rz_ssdr_mode: DEFAULT_RZ_SSDR_MODE,
    },
    validate: {
      schema: value => (value ? null : t('errSchemaRequired')),
    },
  })

  useEffect(() => {
    if (opened) {
      form.setValues({
        schema: defaultSchema,
        district_id: '',
        include_technical_segments: false,
        include_regulated_zones: false,
        include_sign_posts: false,
        exclude_frozen: false,
        rz_ssdr_mode: DEFAULT_RZ_SSDR_MODE,
      })
      form.resetDirty()
      setStep('form')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultSchema])

  // Districts depend on the selected schema, so re-fetch as it changes.
  const { data: districts, isLoading: districtsLoading } = useQuery({
    ...getDistrictMapOptions({ headers: { 'city-sense-control-data-schema': form.values.schema } }),
    enabled: opened && !!form.values.schema,
  })

  const districtOptions = useMemo(
    () =>
      (districts ?? []).map(d => ({
        value: String(d.id),
        label: `${d.district_code} · ${d.subdistrict_code}`,
      })),
    [districts]
  )

  const districtLabel = districtOptions.find(o => o.value === form.values.district_id)?.label ?? t('ssdrAllDistricts')

  const enabledFlags = FLAG_KEYS.filter(key => form.values[key])

  const rzModeOptions = useMemo(
    () =>
      (Object.keys(RZ_SSDR_MODE_LABEL_KEY) as RegulatedZoneSsdrMode[]).map(mode => ({
        value: mode,
        label: t(RZ_SSDR_MODE_LABEL_KEY[mode]),
      })),
    [t]
  )

  const handleClose = () => {
    setStep('form')
    onClose()
  }

  const handleConfirm = () => {
    const districtId = form.values.district_id ? Number(form.values.district_id) : null
    onSubmit(form.values.schema, districtId, {
      include_technical_segments: form.values.include_technical_segments,
      include_regulated_zones: form.values.include_regulated_zones,
      include_sign_posts: form.values.include_sign_posts,
      exclude_frozen: form.values.exclude_frozen,
      rz_ssdr_mode: form.values.rz_ssdr_mode,
    })
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t('computeSsdr')} padding="lg" size="lg" yOffset="5vh">
      <Stack style={{ minHeight: 280 }}>
        {step === 'form' ? (
          <form onSubmit={form.onSubmit(() => setStep('confirm'))}>
            <Stack>
              <Select
                label={t('ssdrSchema')}
                data={schemas.map(s => ({ value: s.schema_name, label: s.schema_name }))}
                searchable
                {...form.getInputProps('schema')}
                data-autofocus
              />
              <Select
                label={t('ssdrDistrict')}
                description={t('ssdrDistrictHelp')}
                placeholder={t('ssdrAllDistricts')}
                data={districtOptions}
                disabled={districtsLoading}
                searchable
                clearable
                {...form.getInputProps('district_id')}
              />
              <Checkbox
                label={t('includeTechnicalSegments')}
                {...form.getInputProps('include_technical_segments', { type: 'checkbox' })}
              />
              <Checkbox
                label={t('includeRegulatedZones')}
                {...form.getInputProps('include_regulated_zones', { type: 'checkbox' })}
              />
              <Select
                label={t('ssdrRzMode')}
                description={t('ssdrRzModeHelp')}
                data={rzModeOptions}
                disabled={!form.values.include_regulated_zones}
                allowDeselect={false}
                {...form.getInputProps('rz_ssdr_mode')}
              />
              <Checkbox
                label={t('includeSignPosts')}
                {...form.getInputProps('include_sign_posts', { type: 'checkbox' })}
              />
              <Checkbox label={t('excludeFrozen')} {...form.getInputProps('exclude_frozen', { type: 'checkbox' })} />
              <Group justify="flex-end" gap="xs" mt="md">
                <Button variant="default" onClick={handleClose}>
                  {t('cancel')}
                </Button>
                <Button type="submit" color="blue" variant="light">
                  {t('compute')}
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack>
            <Text size="sm">{t('confirmComputeBody', { schema: form.values.schema, district: districtLabel })}</Text>
            {enabledFlags.length > 0 ? (
              <List size="sm" withPadding>
                {enabledFlags.map(key => (
                  <List.Item key={key}>{t(FLAG_LABEL_KEY[key])}</List.Item>
                ))}
                {form.values.include_regulated_zones && (
                  <List.Item>
                    {t('ssdrRzMode')}: {t(RZ_SSDR_MODE_LABEL_KEY[form.values.rz_ssdr_mode])}
                  </List.Item>
                )}
              </List>
            ) : (
              <Text size="sm" c="dimmed">
                {t('confirmComputeNoOptions')}
              </Text>
            )}
            <Group justify="flex-end" gap="xs" mt="md">
              <Button variant="default" onClick={() => setStep('form')}>
                {t('back')}
              </Button>
              <Button color="blue" loading={isSubmitting} onClick={handleConfirm}>
                {t('confirm')}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
